import * as React from 'react';
import { useContext, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import TopBar from './TopBar';
import Dock, { DockItem } from './Dock';
import SwitcherMenu, { SwitcherItem } from './SwitcherMenu';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Toaster } from '@/src/shadComponents/ui/sonner';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import OrgContext from '@/src/Authentication/components/OrgContext';

/*
 * V2Shell — the shared chrome shell: .psv2 scope, persisted dark mode,
 * TopBar (org circle · context switcher · account avatar) and the Dock.
 * Chromes supply the context switcher payload; the org and account
 * menus are shell-owned so they exist at every level.
 */

const DARK_KEY = 'psv2-dark';

export interface ShellSwitcher {
    prefix?: string;
    label: string;
    header?: string;
    items: SwitcherItem[];
    footer?: SwitcherItem[];
    searchable?: boolean;
}

interface V2ShellProps {
    switcher: ShellSwitcher;
    items: DockItem[];
    actions?: DockItem[];
    children?: React.ReactNode;
}

export default function V2Shell({
    switcher,
    items,
    actions,
    children,
}: V2ShellProps) {
    const [dark, setDark] = useState(
        () => localStorage.getItem(DARK_KEY) === '1',
    );
    const navigate = useNavigate();
    const { auth, signout } = useAuth() as any;
    const { org } = useUserOrg();
    const { setOrg } = useContext(OrgContext) as any;
    const orgs: any[] = auth?.orgs ?? [];

    const toggleDark = (value: boolean) => {
        setDark(value);
        localStorage.setItem(DARK_KEY, value ? '1' : '0');
    };

    const initials = (value?: string) =>
        (value ?? '?')
            .split(/[\s@._-]+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part: string) => part[0])
            .join('')
            .toUpperCase();

    return (
        /* overflow-x-clip: full-bleed elements (hero chart) may exceed
           the viewport by the scrollbar width — clip, don't scroll */
        <div
            className={
                'psv2 min-h-screen overflow-x-clip pb-32' +
                (dark ? ' dark' : '')
            }
        >
            <TopBar
                left={
                    <>
                        {/* org circle: the tenant's mark, switchable everywhere */}
                        <SwitcherMenu
                            triggerTitle="Switch organization"
                            header="Organization"
                            align="left"
                            trigger={
                                <span className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background">
                                    {(org?.name ?? 'P')[0].toUpperCase()}
                                </span>
                            }
                            items={orgs.map((o: any) => ({
                                key: String(o.id),
                                label: o.name,
                                active: o.id === org?.id,
                                onClick: () => {
                                    if (o.id === org?.id) return;
                                    setOrg(o);
                                    /* clean reload — every page's data is
                                       org-scoped */
                                    window.location.assign('/v2');
                                },
                            }))}
                            footer={[
                                {
                                    key: 'devices',
                                    label: 'Devices',
                                    onClick: () => navigate('/v2/devices'),
                                },
                            ]}
                        />
                        {/* context switcher: section / job */}
                        <SwitcherMenu
                            triggerTitle="Switch context"
                            header={switcher.header}
                            align="left"
                            trigger={
                                <span className="flex items-center gap-[7px] text-[13.5px]">
                                    {switcher.prefix && (
                                        <span className="text-faint">
                                            {switcher.prefix}
                                        </span>
                                    )}
                                    <span className="max-w-[300px] truncate font-semibold tracking-[-0.01em] text-foreground">
                                        {switcher.label}
                                    </span>
                                    <span
                                        aria-hidden
                                        className="text-[10px] text-faint"
                                    >
                                        ▾
                                    </span>
                                </span>
                            }
                            items={switcher.items}
                            footer={switcher.footer}
                            searchable={switcher.searchable}
                        />
                    </>
                }
                right={
                    <SwitcherMenu
                        triggerTitle="Account"
                        align="right"
                        width={230}
                        trigger={
                            <span className="flex h-[27px] w-[27px] items-center justify-center rounded-full bg-secondary text-[10.5px] font-semibold text-muted-foreground">
                                {initials(auth?.username)}
                            </span>
                        }
                        items={[
                            {
                                key: 'classic',
                                label: 'Classic app ↗',
                                onClick: () => navigate('/home'),
                            },
                            {
                                key: 'logout',
                                label: 'Log out',
                                onClick: async () => {
                                    await signout?.().catch(() => null);
                                    navigate('/login');
                                },
                            },
                        ]}
                    >
                        <label className="flex cursor-pointer items-center justify-between gap-2 rounded-[8px] px-2.5 py-2 text-[13px] text-muted-foreground hover:bg-secondary">
                            Dark mode
                            <Switch
                                checked={dark}
                                onCheckedChange={toggleDark}
                            />
                        </label>
                    </SwitcherMenu>
                }
            />
            <Outlet />
            <Dock items={items} actions={actions} />
            {children}
            <Toaster position="bottom-left" />
        </div>
    );
}
